import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Calendar } from 'lucide-react';
import Seo from '../components/Seo';
import { fetchPosts } from '../api';

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch (e) { return ''; }
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try { setPosts(await fetchPosts()); } catch (e) { /* empty */ }
      finally { setLoaded(true); }
    })();
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: '24BetBox Blog',
    description: 'Football betting guides, strategy articles and prediction insights from the 24BetBox tipster team.',
    url: 'https://24betbox.com/blog',
  };

  return (
    <div data-testid="blog-page">
      <Seo
        title="Football Betting Blog | Tips, Guides & Strategy – 24BetBox"
        description="Expert football betting guides and strategy articles: understand Over 2.5 goals, BTTS, accumulators, bankroll management and how to read our daily predictions."
        path="/blog"
        jsonLd={jsonLd}
      />

      <section className="hero-gradient border-b border-[#2e2822]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <span className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-orange-400">
            <BookOpen className="h-3.5 w-3.5" /> Insights & Guides
          </span>
          <h1 className="mt-6 font-heading text-4xl font-extrabold text-white sm:text-5xl lg:text-6xl">The 24BetBox Blog</h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-300">
            Football betting strategy, market explainers and prediction insights from our team of professional tipsters — everything you need to bet smarter.
          </p>
        </div>
      </section>

      <section className="bg-[#15120f] py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          {!loaded ? (
            <div className="rounded-xl border border-[#2e2822] bg-[#1b1712] p-12 text-center text-neutral-400">Loading articles…</div>
          ) : posts.length === 0 ? (
            <div className="rounded-2xl border border-[#2e2822] bg-[#1b1712] p-12 text-center">
              <BookOpen className="mx-auto h-10 w-10 text-orange-500" />
              <h3 className="mt-4 font-heading text-2xl font-extrabold text-white">Articles coming soon</h3>
              <p className="mx-auto mt-2 max-w-md text-neutral-400">Our first betting guides are on the way. In the meantime, check today's free predictions.</p>
              <Link to="/football-predictions-today" className="btn-orange mt-6 inline-flex items-center gap-2 rounded-lg px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide">Today's Tips <ArrowRight className="h-4 w-4" /></Link>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid="blog-post-card"
                  className="card-dark group flex flex-col overflow-hidden rounded-2xl transition-transform duration-200 hover:-translate-y-1 hover:ring-1 hover:ring-orange-500/40"
                >
                  <div className="relative h-48 w-full overflow-hidden bg-[#241e18]">
                    {p.coverImage
                      ? <img src={p.coverImage} alt={p.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      : <div className="flex h-full w-full items-center justify-center"><BookOpen className="h-10 w-10 text-orange-500/40" /></div>}
                    {p.category && <span className="absolute left-3 top-3 rounded-full bg-orange-500 px-3 py-1 text-[11px] font-extrabold uppercase text-[#15120f]">{p.category}</span>}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h2 className="font-heading text-xl font-extrabold leading-snug text-white group-hover:text-orange-400">{p.title}</h2>
                    {p.excerpt && <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-neutral-400">{p.excerpt}</p>}
                    <div className="mt-4 flex items-center gap-3 text-xs text-neutral-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(p.created_at)}</span>
                      <span className="ml-auto inline-flex items-center gap-1 font-bold text-orange-400">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
